import { Box, Divider, FormField, InfoIcon, RadioGroup, Text } from '@wix/design-system';
import React, { useContext, useMemo } from 'react';
import { RicosContext, ZIndexContext } from 'ricos-context';
import type {
  PollData,
  PollData_Poll_Settings,
  PollData_Poll_Settings_Permissions,
} from 'ricos-schema/next';
import {
  PollData_Poll_Settings_Permissions_ViewRole,
  PollData_Poll_Settings_Permissions_VoteRole,
} from 'ricos-schema/next';
import { ToggleButton } from 'wix-rich-content-ui-components';

import {
  POLL_VISIBILITY_OPTION_CREATOR,
  POLL_VISIBILITY_OPTION_EVERYONE,
  POLL_VISIBILITY_OPTION_VOTERS,
  POLLS_EACH_PERSON_VOTE_TOGGLE,
  POLLS_MULTIPLE_ANSWERS_TOGGLE,
  POLLS_VOTE_COUNT_TOGGLE,
} from '../../dataHooks';
import type { PollPluginEditorConfig } from '../../types';
import { Section } from './section';

type PollSettingsSectionProps = {
  componentData: PollData;
  config: PollPluginEditorConfig;
  updateRicosDocument: (data: PollData) => void;
};

export const PollSettingsSection: React.FC<PollSettingsSectionProps> = ({
  componentData,
  updateRicosDocument,
  config,
}) => {
  const { t } = useContext(RicosContext);

  const updatePermissions = (permissions: PollData_Poll_Settings_Permissions) => {
    updateRicosDocument({
      poll: {
        options: [],
        ...componentData.poll,
        settings: {
          ...componentData.poll?.settings,
          permissions: {
            ...componentData.poll?.settings?.permissions,
            ...permissions,
          },
        },
      },
    });
  };

  const updateSettings = (settings: PollData_Poll_Settings) => {
    updateRicosDocument({
      poll: {
        options: [],
        ...componentData.poll,
        settings: {
          ...componentData?.poll?.settings,
          ...settings,
        },
      },
    });
  };

  const pollPermissions = componentData.poll?.settings?.permissions;

  return (
    <>
      <Section title={t('Poll_PollSettings_Tab_Settings_Section_Voting_Header')}>
        {config.showVoteRoleSetting && (
          <WhoCanVoteRadioGroup
            value={pollPermissions?.vote}
            onChange={vote => updatePermissions({ vote })}
          />
        )}
        <ToggleButton
          dataHook={POLLS_MULTIPLE_ANSWERS_TOGGLE}
          title={t('Poll_PollSettings_Tab_Settings_Section_Voting_Multiselect')}
          checked={!!pollPermissions?.allowMultipleVotes}
          onChange={() =>
            updatePermissions({ allowMultipleVotes: !pollPermissions?.allowMultipleVotes })
          }
        />
      </Section>
      <Divider />

      <Section title={t('Poll_PollSettings_Tab_Settings_Section_Results_Header')}>
        <WhoCanSeePollResultsRadioGroup
          value={pollPermissions?.view}
          onChange={view => updatePermissions({ view })}
        />
        {pollPermissions?.view !== PollData_Poll_Settings_Permissions_ViewRole.CREATOR && (
          <>
            <ToggleButton
              dataHook={POLLS_VOTE_COUNT_TOGGLE}
              title={t('Poll_PollSettings_Tab_Settings_Section_Results_VoteVisibility')}
              checked={!!componentData.poll?.settings?.showVotesCount}
              onChange={() =>
                updateSettings({
                  showVoters: !componentData.poll?.settings?.showVotesCount,
                  showVotesCount: !componentData.poll?.settings?.showVotesCount,
                })
              }
            />
            {componentData.poll?.settings?.showVotesCount && (
              <ToggleButton
                dataHook={POLLS_EACH_PERSON_VOTE_TOGGLE}
                title={t('Poll_PollSettings_Tab_Settings_Section_Results_VoterAnonymous')}
                checked={!!componentData.poll?.settings?.showVoters}
                onChange={() =>
                  updateSettings({ showVoters: !componentData.poll?.settings?.showVoters })
                }
              />
            )}
          </>
        )}
      </Section>
    </>
  );
};

const WhoCanVoteRadioGroup: React.FC<{
  value: PollData_Poll_Settings_Permissions_VoteRole | undefined;
  onChange: (data: PollData_Poll_Settings_Permissions_VoteRole) => void;
}> = ({ value, onChange }) => {
  const { t } = useContext(RicosContext);
  const voteRoleOptions = useMemo(
    () => [
      {
        value: PollData_Poll_Settings_Permissions_VoteRole.SITE_MEMBERS,
        label: t('Poll_PollSettings_Tab_Settings_Section_Voting_Permission_SiteMember'),
      },
      {
        value: PollData_Poll_Settings_Permissions_VoteRole.ALL,
        label: t('Poll_PollSettings_Tab_Settings_Section_Voting_Permission_All'),
      },
    ],
    [t]
  );

  return (
    <RadioGroupWrapper
      label={t('Poll_PollSettings_Tab_Settings_Section_Voting_Permission_Title')}
      value={value}
      onChange={onChange}
      options={voteRoleOptions}
    />
  );
};

const WhoCanSeePollResultsRadioGroup: React.FC<{
  value: PollData_Poll_Settings_Permissions_ViewRole | undefined;
  onChange: (data: PollData_Poll_Settings_Permissions_ViewRole) => void;
}> = ({ value, onChange }) => {
  const { t } = useContext(RicosContext);

  const viewRoleOptions = useMemo(
    () => [
      {
        dataHook: POLL_VISIBILITY_OPTION_EVERYONE,
        value: PollData_Poll_Settings_Permissions_ViewRole.EVERYONE,
        label: (
          <TextWithInfo
            text={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_All')}
            infoContent={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_All_Tooltip')}
          />
        ),
      },
      {
        dataHook: POLL_VISIBILITY_OPTION_VOTERS,
        value: PollData_Poll_Settings_Permissions_ViewRole.VOTERS,
        label: (
          <TextWithInfo
            text={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_Voters')}
            infoContent={t(
              'Poll_PollSettings_Tab_Settings_Section_Results_Permission_Voters_Tooltip'
            )}
          />
        ),
      },
      {
        dataHook: POLL_VISIBILITY_OPTION_CREATOR,
        value: PollData_Poll_Settings_Permissions_ViewRole.CREATOR,
        label: (
          <TextWithInfo
            text={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_Owner')}
            infoContent={t(
              'Poll_PollSettings_Tab_Settings_Section_Results_Permission_Owner_Tooltip'
            )}
          />
        ),
      },
    ],
    [t]
  );

  return (
    <RadioGroupWrapper
      label={t('Poll_PollSettings_Tab_Settings_Section_Results_Permission_Title')}
      value={value}
      onChange={onChange}
      options={viewRoleOptions}
    />
  );
};

const RadioGroupWrapper: React.FC<{
  label: string;
  value: string | undefined;
  onChange: (data: string) => void;
  options: { value: string; label: React.JSX.Element | string; dataHook?: string }[];
}> = ({ label, value, onChange, options }) => {
  return (
    <Box marginTop="SP3" marginBottom="SP2">
      <FormField label={label}>
        <RadioGroup value={value} onChange={onChange} size="small">
          {options.map(({ value, label, dataHook }) => (
            <RadioGroup.Radio key={value} value={value} dataHook={dataHook}>
              {label}
            </RadioGroup.Radio>
          ))}
        </RadioGroup>
      </FormField>
    </Box>
  );
};

const TextWithInfo: React.FC<{ text: string; infoContent: string }> = ({ text, infoContent }) => {
  const zIndexService = useContext(ZIndexContext);

  return (
    <Box verticalAlign="middle" gap="SP1">
      <Text size="small">{text}</Text>
      <InfoIcon
        size="small"
        content={infoContent}
        tooltipProps={{
          appendTo: el => Boolean(el.getAttribute('data-hook')?.includes('ricos-portal')),
          zIndex: zIndexService.getZIndex('TOOLTIP'),
        }}
      />
    </Box>
  );
};
